import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import './App.css';
import Header from './components/Header';
import FiltersBar from './components/FiltersBar';
import StartupMap from './components/StartupMap';
import CardsCarousel from './components/CardsCarousel';
import StartupDetail from './components/StartupDetail';
import MobileHomeSheet from './components/MobileHomeSheet';
import MobileDetailSheet from './components/MobileDetailSheet';
import useIsMobile from './hooks/useIsMobile';
import SubmitModal from './components/SubmitModal';
import EventsPanel from './components/EventsPanel';
import FeedbackModal from './components/FeedbackModal';
import ChatWidget from './components/ChatWidget';
import Admin from './pages/Admin';
import CompanyProfile from './pages/CompanyProfile';
import { AuthProvider } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import { getStartups, getNeighborhoods, getTechEvents } from './services/api';

function MapView() {
  const [allStartups, setAllStartups] = useState([]);
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [techEvents, setTechEvents] = useState([]);
  const [loadingStartups, setLoadingStartups] = useState(true);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [eventsLoaded, setEventsLoaded] = useState(false);

  const [search, setSearch] = useState('');
  const [stages, setStages] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [eventsOnly, setEventsOnly] = useState(false);
  const [ycOnly, setYcOnly] = useState(false);
  const [sort, setSort] = useState('name');
  const [clusterMode, setClusterMode] = useState(false);

  const [activeId, setActiveId] = useState(null);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [eventsPanelOpen, setEventsPanelOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const isMobile = useIsMobile();

  const loadStartups = useCallback(async () => {
    try {
      setLoadingStartups(true);
      const [s, n] = await Promise.all([getStartups(), getNeighborhoods()]);
      setAllStartups(s);
      setNeighborhoods(n);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingStartups(false);
    }
  }, []);

  useEffect(() => { loadStartups(); }, [loadStartups]);

  const loadEvents = useCallback(async (force = false) => {
    if (!force && eventsLoaded) return;
    try {
      setLoadingEvents(true);
      const data = await getTechEvents(60);
      setTechEvents(data);
      setEventsLoaded(true);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingEvents(false);
    }
  }, [eventsLoaded]);

  // Open events panel when toggle turns on
  useEffect(() => {
    if (eventsOnly) {
      setEventsPanelOpen(true);
      loadEvents();
    } else {
      setEventsPanelOpen(false);
    }
  }, [eventsOnly, loadEvents]);

  const filtered = useMemo(() => {
    let list = allStartups.filter((s) => {
      if (search) {
        const q = search.toLowerCase();
        if (
          !s.name.toLowerCase().includes(q) &&
          !s.sector.toLowerCase().includes(q) &&
          !s.description.toLowerCase().includes(q) &&
          !(s.neighborhood || '').toLowerCase().includes(q)
        )
          return false;
      }
      if (stages.length && !stages.includes(s.stage)) return false;
      if (sectors.length && !sectors.includes(s.sector)) return false;
      if (ycOnly && !s.yc) return false;
      return true;
    });

    list = [...list].sort((a, b) => {
      const key = sort;
      const av = (a[key] || '').toString().toLowerCase();
      const bv = (b[key] || '').toString().toLowerCase();
      return av.localeCompare(bv);
    });
    return list;
  }, [allStartups, search, stages, sectors, ycOnly, sort]);

  // Re-compute clusters from the visible (filtered) list
  const visibleClusters = useMemo(() => {
    const ids = new Set(filtered.map((s) => s.id));
    return neighborhoods
      .map((n) => {
        const matching = n.startup_ids.filter((id) => ids.has(id));
        if (!matching.length) return null;
        // Recompute centroid from filtered set
        const subset = filtered.filter((s) => s.neighborhood === n.neighborhood);
        if (!subset.length) return null;
        const lat = subset.reduce((a, s) => a + s.lat, 0) / subset.length;
        const lng = subset.reduce((a, s) => a + s.lng, 0) / subset.length;
        return {
          neighborhood: n.neighborhood,
          count: matching.length,
          lat, lng,
          startup_ids: matching,
          sample: subset.slice(0, 4).map((s) => ({ id: s.id, name: s.name, color: s.color })),
        };
      })
      .filter(Boolean)
      .sort((a, b) => b.count - a.count);
  }, [filtered, neighborhoods]);

  useEffect(() => {
    if (activeId && !filtered.find((s) => s.id === activeId)) {
      setActiveId(null);
    }
  }, [filtered, activeId]);

  const active = filtered.find((s) => s.id === activeId);

  const handleSubmitted = async () => {
    // Don't refresh public map until admin approves
  };

  const handleClusterZoomIn = (cluster) => {
    // When clicking a cluster, exit cluster mode so we see the markers around it
    setClusterMode(false);
  };

  return (
    <div className="App relative h-screen w-screen overflow-hidden bg-slate-100">
      <StartupMap
        startups={filtered}
        clusters={visibleClusters}
        clusterMode={clusterMode}
        activeId={activeId}
        onSelect={setActiveId}
        onClusterZoom={handleClusterZoomIn}
      />

      <Header onSubmit={() => setSubmitOpen(true)} onFeedback={() => setFeedbackOpen(true)} />

      <FiltersBar
        search={search}
        setSearch={setSearch}
        stages={stages}
        setStages={setStages}
        sectors={sectors}
        setSectors={setSectors}
        eventsOnly={eventsOnly}
        setEventsOnly={setEventsOnly}
        ycOnly={ycOnly}
        setYcOnly={setYcOnly}
        sort={sort}
        setSort={setSort}
        clusterMode={clusterMode}
        setClusterMode={setClusterMode}
        onAskAi={() => setChatOpen(true)}
      />

      <div className="hidden sm:block absolute bottom-4 left-4 z-[900] bg-white/95 backdrop-blur-md border border-slate-200/60 px-3 py-1.5 rounded-full text-[12px] font-medium text-slate-700 shadow-md pointer-events-none">
        {loadingStartups ? (
          <span>Loading…</span>
        ) : clusterMode ? (
          <>
            <span className="font-semibold text-slate-900">{visibleClusters.length}</span> neighborhoods ·{' '}
            <span className="font-semibold text-slate-900">{filtered.length}</span> startups
          </>
        ) : (
          <>
            <span className="font-semibold text-slate-900">{filtered.length}</span> startups
          </>
        )}
      </div>

      {active && !eventsPanelOpen && !clusterMode && (
        isMobile ? (
          <MobileDetailSheet
            startup={active}
            onClose={() => setActiveId(null)}
            onReportIssue={() => setFeedbackOpen(true)}
          />
        ) : (
          <StartupDetail
            startup={active}
            onClose={() => setActiveId(null)}
            onUpdated={loadStartups}
          />
        )
      )}

      <EventsPanel
        open={eventsPanelOpen}
        events={techEvents}
        loading={loadingEvents}
        onClose={() => {
          setEventsPanelOpen(false);
          setEventsOnly(false);
        }}
        onRefresh={() => loadEvents(true)}
      />

      <CardsCarousel
        startups={filtered}
        activeId={activeId}
        onSelect={setActiveId}
      />

      <MobileHomeSheet
        startups={filtered}
        activeId={activeId}
        onSelect={setActiveId}
        search={search}
        setSearch={setSearch}
        stages={stages}
        setStages={setStages}
        sectors={sectors}
        setSectors={setSectors}
        eventsOnly={eventsOnly}
        setEventsOnly={setEventsOnly}
        ycOnly={ycOnly}
        setYcOnly={setYcOnly}
        sort={sort}
        setSort={setSort}
        clusterMode={clusterMode}
        setClusterMode={setClusterMode}
        onAskAi={() => setChatOpen(true)}
      />

      <SubmitModal
        open={submitOpen}
        onClose={() => setSubmitOpen(false)}
        onSubmitted={handleSubmitted}
      />

      <FeedbackModal open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />

      <ChatWidget open={chatOpen} setOpen={setChatOpen} />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/" element={<MapView />} />
          <Route path="/auth/callback" element={<MapView />} />
          <Route path="/company/:id" element={<CompanyProfile />} />
          <Route path="/admin" element={<Admin />} />
        </Routes>
        <Toaster position="bottom-center" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
